import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { settingsService } from "@shared/src/services/settings.service";
import { ixToast } from "@shared/src/utils/ixToast";
import { AuthParams } from "@shared/src/hooks/auth/types";
import { IxSigner } from "@shared/src/stores/useAuthStore";
import { BackendAccount } from "@sdk/shared/types/settings.types";

export function useAccountManagement() {
  const {
    setActiveAccount,
    activeAccount,
    setSelectedView,
    setTempAuth,
    tempAuth,
    setAccountLinkingStatus,
    accountLinkingStatus,
  } = useAuthStore();

  async function handleAccountSetup({
    jwt,
    address,
    signer,
    accountType,
    params,
    existingAccount,
  }: {
    jwt: string;
    address: `0x${string}`;
    signer: IxSigner;
    accountType: "sca" | "eoa";
    params: AuthParams & { connectorIcon: string };
    existingAccount: BackendAccount | null | undefined;
  }) {
    // if (isMainnetDomain()) {
    //   // Determine which ID to use for whitelist check based on auth type
    //   const checkId =
    //     params.type === "email"
    //       ? params.email
    //       : params.type === "passkey"
    //       ? params.email
    //       : params.type === "oauth" && useAuthStore.getState().email
    //       ? useAuthStore.getState().email
    //       : address;

    //   const whitelistResponse = await settingsService.isWhitelisted({
    //     id: checkId,
    //     jwt,
    //   });

    //   // Handle whitelist check error or not whitelisted
    //   if (whitelistResponse.error) {
    //     // Store auth info in tempAuth following account_linking pattern
    //     setTempAuth({
    //       jwt,
    //       address,
    //       id: existingAccount?.id ?? 0,
    //       isAuthenticated: true,
    //       signer,
    //       type: accountType,
    //       connectorIcon: params.connectorIcon,
    //       authParams: params,
    //     });
    //     setSelectedView("whitelist_check");
    //     return;
    //   }
    // }

    // Only get account details if not on mainnet or if whitelisted

    if (activeAccount?.jwt && accountLinkingStatus) {
      await handleLinkAccount({
        activeAccountJwt: activeAccount.jwt,
        newAccountJwt: jwt,
        activeAccountAddress: activeAccount.address,
        newAccountAddress: address,
        signer,
        accountType,
        params,
      });
    }

    if (tempAuth?.jwt && !existingAccount) {
      await handleLinkAccount({
        newAccountJwt: tempAuth.jwt,
        activeAccountAddress: address,
        newAccountAddress: tempAuth.address,
        activeAccountJwt: jwt,
        signer,
        accountType,
        params,
      });
    } else if (!existingAccount) {
      await handleNewAccount({
        jwt,
        address,
        signer,
        accountType,
        params,
      });
    } else {
      await handleExistingAccount({
        accountData: existingAccount,
        jwt,
        address,
        signer,
        accountType,
        params,
      });
    }
  }

  interface LinkAccountParams {
    activeAccountAddress: `0x${string}`;
    newAccountAddress: `0x${string}`;
    activeAccountJwt: string;
    newAccountJwt: string;
    signer: IxSigner;
    accountType: "sca" | "eoa";
    params: AuthParams & { connectorIcon: string };
  }
  interface NewAccountParams {
    jwt: string;
    address: `0x${string}`;
    signer: IxSigner;
    accountType: "sca" | "eoa";
    params: AuthParams & { connectorIcon: string };
  }

  async function handleNewAccount({ jwt, address, signer, accountType, params }: NewAccountParams) {
    setTempAuth({
      jwt,
      address: address as `0x${string}`,
      id: 0,
      isAuthenticated: true,
      signer,
      type: accountType as "sca" | "eoa",
      connectorIcon: params.connectorIcon,
      authParams: params,
    });
    console.log("Switching to account linking view...");
    setSelectedView("account_linking");

    setAccountLinkingStatus("initial");
    ixToast.dismiss();
  }

  async function handleExistingAccount({
    accountData,
    jwt,
    address,
    signer,
    accountType,
    params,
  }: NewAccountParams & { accountData: BackendAccount }) {
    console.log("Setting active account...", accountLinkingStatus);
    await setActiveAccount(
      {
        address,
        id: accountData.id,
        isAuthenticated: true,
        type: accountType,
        username: accountData.username,
        signer,
        jwt,
        connectorIcon: params.connectorIcon,
        authParams: params,
        backendAccount: accountData,
      },
      !!accountLinkingStatus,
    );
    params.onSuccess?.();
  }

  async function handleLinkAccount({
    activeAccountAddress,
    newAccountAddress,
    signer,
    accountType,
    params,
    activeAccountJwt,
    newAccountJwt,
  }: LinkAccountParams) {
    console.log("Linking accounts...");
    const activeAccount = useAuthStore.getState().activeAccount;
    if (!tempAuth && !activeAccount?.jwt) throw new Error("No Sign in found");
    const linkingresponse = await settingsService.linkAccount({
      existing: activeAccountJwt,
      new: newAccountJwt,
    });

    if (linkingresponse.error || !linkingresponse.data) {
      console.log("Errorlinking accounts:", linkingresponse.error);
      ixToast.error(linkingresponse.error);
      throw linkingresponse.error;
    }

    console.log("Linking response:", linkingresponse);
    await setActiveAccount(
      {
        jwt: activeAccountJwt,
        address: activeAccountAddress,
        id: linkingresponse.data.id,
        isAuthenticated: true,
        username: linkingresponse.data?.username,
        signer,
        type: accountType as "sca" | "eoa",
        connectorIcon: activeAccount?.connectorIcon || params.connectorIcon,
        authParams: activeAccount?.authParams || params,
        backendAccount: linkingresponse.data,
      },
      true,
    );
    ixToast.dismiss();
    setSelectedView("account_linking");
    setAccountLinkingStatus("success");
  }

  return { handleAccountSetup };
}
